import { useSyncExternalStore } from 'react';
import { scopedStorage } from './standaloneStorage';
import { isUserDataKey } from './userDataKeys';

export interface MemberInfo {
  isMember: boolean;
  planType: 'none' | 'trial' | 'monthly' | 'quarterly' | 'yearly';
  startDate?: string;
  expireDate?: string;
  daysRemaining?: number;
  trialClaimed?: boolean;
  trialExpireDate?: string;
}
export interface AccountUser {
  id: string;
  username: string;
  name: string;
  member: MemberInfo;
  trialClaimed: boolean;
  trialExpireDate?: string;
}
const freeMember: MemberInfo = { isMember: false, planType: 'none' };
let snapshot: { user: AccountUser | null; ready: boolean; version: number } = { user: null, ready: false, version: 0 };
let csrf = '';
let sessionVersion = 0;
let refreshing: Promise<void> | undefined;
let initialization: Promise<void> | undefined;
const listeners = new Set<() => void>();

function publish(user: AccountUser | null) {
  const previous = snapshot.user?.id;
  if (snapshot.ready && previous !== user?.id) scopedStorage.removeItem('active_workout');
  snapshot = { user, ready: true, version: sessionVersion };
  listeners.forEach(listener => listener());
}

export function useAccountSession() {
  return useSyncExternalStore(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => snapshot);
}
export function currentAccount() { return snapshot.user; }
export function currentMembership() { return snapshot.user?.member || freeMember; }

export class AccountApiError extends Error {
  constructor(message: string, public status: number, public result: Record<string, unknown>) { super(message); }
}
export async function accountApi(path: string, data?: unknown, options?: { accountId?: string; signal?: AbortSignal }) {
  const requestAccount = snapshot.user?.id;
  const requestVersion = sessionVersion;
  if (options?.accountId && requestAccount !== options.accountId) throw new Error('账号已切换');
  const response = await fetch('/api' + path, {
    method: data === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: { ...(data === undefined ? {} : { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }),
      ...(options?.accountId ? { 'X-Account-ID': options.accountId } : {}) },
    body: data === undefined ? undefined : JSON.stringify(data),
    signal: options?.signal,
  });
  const result = await response.json();
  if (!response.ok) {
    if (response.status === 401 && path !== '/auth/login' && sessionVersion === requestVersion) { csrf = ''; sessionVersion++; publish(null); }
    if (response.status === 403 && sessionVersion === requestVersion && snapshot.user && path !== '/auth/session') {
      await refreshAccountSession();
    }
    throw new AccountApiError(result.error || '连接失败，请重试', response.status, result);
  }
  return result;
}

export function refreshAccountSession() {
  refreshing ??= (async () => {
    const version = sessionVersion;
    try {
      const result = await accountApi('/auth/session');
      if (sessionVersion !== version) return;
      if (csrf !== result.csrf || snapshot.user?.id !== result.user?.id) sessionVersion++;
      csrf = result.csrf; publish(result.user);
    } catch { /* A network failure retains the current account and its pending records. */ }
  })().finally(() => { refreshing = undefined; });
  return refreshing;
}

export function initializeAccount() {
  initialization ??= (async () => {
    try { const result = await accountApi('/auth/session'); csrf = result.csrf; sessionVersion++; publish(result.user); }
    catch { publish(null); }
  })();
  return initialization;
}

function importVisitorRecords(userId: string) {
  const claim = scopedStorage.getItem('visitor_records_owner');
  if (claim) return;
  // Claim before copying: even a quota failure must not offer A's old records to B.
  scopedStorage.setItem('visitor_records_owner', userId);
  // Copy browser-local guest records only on this device's first registration.
  const keys = Array.from({ length: scopedStorage.length }, (_, index) => scopedStorage.key(index));
  for (const key of keys) {
    if (!key?.startsWith('u_anon:')) continue;
    const suffix = key.slice('u_anon:'.length);
    if (!isUserDataKey(suffix)) continue;
    const target = `u_${userId}:${suffix}`;
    const value = scopedStorage.getItem(key);
    if (value !== null && scopedStorage.getItem(target) === null) scopedStorage.setItem(target, value);
  }
}

export async function signIn(mode: 'login' | 'register', data: { username: string; password: string; name?: string }) {
  const result = await accountApi('/auth/' + mode, data);
  if (mode === 'register') {
    try { importVisitorRecords(result.user.id); } catch { /* Guest originals remain intact if local storage is full. */ }
  }
  csrf = result.csrf; sessionVersion++; publish(result.user);
  return result.user as AccountUser;
}
export async function signOut() { await accountApi('/auth/logout', {}); csrf = ''; sessionVersion++; publish(null); }

export async function heartbeat(active: boolean) {
  if (!snapshot.user) return;
  const userId = snapshot.user.id;
  const version = sessionVersion;
  const result = await accountApi('/auth/heartbeat', { active });
  if (snapshot.user?.id === userId && sessionVersion === version) publish(result.user);
}

export async function requestMembership(plan: 'monthly' | 'quarterly' | 'yearly') {
  if (!snapshot.user) throw new Error('请先登录账号，再申请开通会员');
  return accountApi('/member/request', { plan });
}
